'use client';

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '@/components/ui/AppIcon';

const servicesData = [
    {
        id: 'web-dev',
        title: 'Web Dev Studio',
        fullTitle: 'Web Development Studio',
        description: 'Full-stack web solutions with cutting-edge technologies and responsive design that delivers exceptional user experiences.',
        icon: 'CodeBracketIcon',
        color: 'from-blue-500 to-indigo-600',
        bg: 'bg-indigo-50',
        geom: 'cube'
    },
    {
        id: 'mobile-app',
        title: 'Mobile Apps',
        fullTitle: 'Mobile App Development',
        description: 'Native and cross-platform mobile applications that engage users with intuitive interfaces and seamless performance.',
        icon: 'DevicePhoneMobileIcon',
        color: 'from-violet-500 to-purple-600',
        bg: 'bg-violet-50',
        geom: 'sphere'
    },
    {
        id: 'brand-lab',
        title: 'Brand Lab',
        fullTitle: 'Brand Visibility Lab',
        description: 'Strategic brand optimization services identifying high-potential opportunities to maximize your online presence.',
        icon: 'ChartBarIcon',
        color: 'from-amber-500 to-orange-600',
        bg: 'bg-amber-50',
        geom: 'pyramid'
    },
    {
        id: 'ecosystem',
        title: 'Digital Ecosystem',
        fullTitle: 'Complete Digital Ecosystem',
        description: 'End-to-end digital transformation combining web, mobile, and brand strategies for comprehensive business growth.',
        icon: 'RocketLaunchIcon',
        color: 'from-emerald-500 to-teal-600',
        bg: 'bg-emerald-50',
        geom: 'torus'
    }
];

const MobileServiceCatalog = () => {
    const [selectedId, setSelectedId] = useState<string | null>(null);

    const handleDragEnd = (event: any, info: any) => {
        if (info.offset.y > 100) {
            setSelectedId(null);
        }
    };

    // Lock body scroll when modal is open
    React.useEffect(() => {
        if (selectedId) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [selectedId]);

    const render3DShape = (type: string) => {
        const baseClass = "absolute w-full h-full preserve-3d opacity-30 animate-[spin_8s_linear_infinite]";
        switch (type) {
            case 'cube':
                return (
                    <div className={`${baseClass}`}>
                        <div className="absolute inset-0 bg-blue-500/20 translate-z-4 border border-blue-400" />
                        <div className="absolute inset-0 bg-blue-500/20 -translate-z-4 border border-blue-400" />
                    </div>
                );
            case 'pyramid':
                return (
                    <div className={`${baseClass}`}>
                        <div className="absolute inset-0 bg-amber-500/20 rotate-45 transform skew-x-12 border border-amber-400" />
                    </div>
                );
            default:
                return (
                    <div className={`${baseClass}`}>
                        <div className="absolute inset-0 rounded-full border-2 border-dashed border-slate-400/50 rotate-x-45" />
                        <div className="absolute inset-0 rounded-full border-2 border-slate-400/20" />
                    </div>
                );
        }
    };

    return (
        <section className="py-20 px-4 pb-32 min-h-screen bg-slate-50">

            {/* Header */}
            <div className="mb-10 pl-2">
                <span className="inline-block px-3 py-1 rounded-full bg-white border border-slate-200 text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-3 shadow-sm">
                    Our Expertise
                </span>
                <h2 className="text-4xl font-black text-slate-900 tracking-tight leading-[1.1] mb-4">
                    World Class <br />
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-blue-600">
                        Services.
                    </span>
                </h2>
                <p className="text-lg text-slate-600 font-medium leading-relaxed max-w-[90%]">
                    Meticulously crafted digital experiences designed to define the future of your brand.
                </p>
            </div>

            {/* Bento Grid layout */}
            <div className="grid grid-cols-2 gap-4">
                {servicesData.map((service, i) => (
                    <motion.div
                        layoutId={`card-${service.id}`}
                        key={service.id}
                        onClick={() => setSelectedId(service.id)}
                        className={`relative overflow-hidden rounded-[2rem] p-5 cursor-pointer shadow-sm border border-slate-100 ${service.bg} active:scale-95 transition-transform h-[220px] flex flex-col justify-between`}
                        transition={{ type: "spring", duration: 0.5, bounce: 0.1 }}
                    >
                        {/* 3D Icon Placeholder */}
                        <motion.div
                            layoutId={`icon-container-${service.id}`}
                            className={`w-12 h-12 rounded-2xl bg-white shadow-sm flex items-center justify-center text-slate-700`}
                        >
                            <Icon name={service.icon as any} className="w-6 h-6" />
                        </motion.div>

                        {/* Title */}
                        <div>
                            <motion.h3
                                layoutId={`title-${service.id}`}
                                className="font-bold text-lg text-slate-900 leading-tight"
                            >
                                {service.title}
                            </motion.h3>
                            <motion.p
                                layoutId={`subtitle-${service.id}`}
                                className="text-xs text-slate-500 mt-1 font-medium"
                            >
                                Tap to expand
                            </motion.p>
                        </div>

                        {/* Decorative Gradient Blob */}
                        <div className={`absolute -right-4 -bottom-4 w-24 h-24 bg-gradient-to-br ${service.color} opacity-20 blur-xl rounded-full pointer-events-none`} />
                    </motion.div>
                ))}
            </div>

            {/* Expanded View (Modal) */}
            <AnimatePresence>
                {selectedId && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.4, ease: "easeInOut" }}
                            className="fixed inset-0 bg-white/80 backdrop-blur-md z-[60]"
                            onClick={() => setSelectedId(null)}
                        />
                        <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center pointer-events-none p-0 sm:p-10">
                            {servicesData.map((service) => {
                                if (service.id !== selectedId) return null;
                                return (
                                    <motion.div
                                        layoutId={`card-${service.id}`}
                                        key={service.id}
                                        drag="y"
                                        dragConstraints={{ top: 0, bottom: 0 }}
                                        dragElastic={{ top: 0, bottom: 0.2 }}
                                        onDragEnd={handleDragEnd}
                                        transition={{ type: "spring", duration: 0.5, bounce: 0.1 }}
                                        className="pointer-events-auto w-full h-[85vh] sm:h-auto sm:max-w-lg bg-white rounded-t-[2.5rem] sm:rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col relative"
                                    >
                                        {/* Close Button */}
                                        <button
                                            onClick={(e) => { e.stopPropagation(); setSelectedId(null); }}
                                            className="absolute top-6 right-6 z-50 w-12 h-12 bg-slate-900/90 backdrop-blur-md rounded-full flex items-center justify-center text-white shadow-lg hover:bg-slate-800 active:scale-90 transition-all border border-white/20"
                                        >
                                            <Icon name="XMarkIcon" className="w-6 h-6" />
                                        </button>

                                        {/* Header / Hero of Card (Draggable Area) */}
                                        <div
                                            className={`relative h-64 ${service.bg} p-8 flex flex-col justify-center items-center overflow-hidden touch-none cursor-grab active:cursor-grabbing`}
                                        >
                                            {/* Drag Handle */}
                                            <div className="absolute top-4 left-1/2 -translate-x-1/2 w-12 h-1.5 bg-slate-900/10 rounded-full z-30" />
                                            {/* Big 3D Shape Background */}
                                            <div className="absolute inset-0 flex items-center justify-center opacity-30 scale-150">
                                                {render3DShape(service.geom)}
                                            </div>

                                            <motion.div
                                                layoutId={`icon-container-${service.id}`}
                                                className="w-20 h-20 rounded-3xl bg-white shadow-xl flex items-center justify-center text-slate-900 relative z-10 mb-6"
                                            >
                                                <Icon name={service.icon as any} className="w-10 h-10" />
                                            </motion.div>

                                            <motion.h3
                                                layoutId={`title-${service.id}`}
                                                className="font-black text-3xl text-center text-slate-900 relative z-10"
                                            >
                                                {service.fullTitle}
                                            </motion.h3>
                                        </div>

                                        {/* Body Content */}
                                        <div className="p-8 pb-12 bg-white flex-1 overflow-y-auto">
                                            <motion.div
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: 0.2 }}
                                            >
                                                <div className="inline-block px-3 py-1 rounded-lg bg-slate-100 text-slate-600 text-xs font-bold uppercase tracking-wider mb-6">
                                                    About Service
                                                </div>
                                                <p className="text-xl text-slate-600 leading-relaxed font-medium mb-10">
                                                    {service.description}
                                                </p>

                                                <button className={`w-full py-4 rounded-xl font-bold text-white shadow-lg bg-gradient-to-r ${service.color} active:scale-[0.98] transition-all flex items-center justify-center gap-2`}>
                                                    Get Started
                                                    <Icon name="ArrowRightIcon" className="w-5 h-5" />
                                                </button>
                                            </motion.div>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </>
                )}
            </AnimatePresence>
        </section>
    );
};

export default MobileServiceCatalog;
